import { Component } from '@angular/core';
import { OptionsListMulti, Row, TemplateItemEditorComponent, TemplateItemEditorModalComponent } from 'gung-common';

@Component({
  selector: 'lib-standard-template-item-editor',
  standalone: false,
  templateUrl: './standard-template-item-editor.component.html',
  styleUrl: './standard-template-item-editor.component.scss'
})
export class StandardTemplateItemEditorComponent extends TemplateItemEditorComponent {
  private rowUiIdCounter = 0;

  editedRows = new Set<Row>();
  editedRowsHeaders = new Set<Row>();

  setTemplate(templateId: string) {
    super.setTemplate(templateId);

    // BaseInputDirective.writeValue ignores null/undefined, so optional fields
    // must be normalized here or the input keeps showing the previous template's value
    if (this.selectedTemplate) {
      this.selectedTemplate.groupName = this.selectedTemplate.groupName ?? '';
      this.selectedTemplate.description = this.selectedTemplate.description ?? '';
    }

    for (const row of this.rows) {
      this.ensureRowUiState(row);
    }

    // Reset after Angular finishes initial ngModel bindings (checkboxes fire ngModelChange on init)
    setTimeout(() => {
      this.editedRows = new Set<Row>();
      this.editedRowsHeaders = new Set<Row>();
      this.editedTemplatesId = [];
      this.editedAliasId = [];
    }, 0);
  }

  override onSave() {
    super.onSave();
    this.editedRows = new Set<Row>();
    this.editedRowsHeaders = new Set<Row>();
  }

  unsavedRow(row: Row, templateId: string, isHeader = false) {
    this.unsaved(templateId);
    if (isHeader) {
      this.editedRowsHeaders = new Set(this.editedRowsHeaders).add(row);
    } else {
      this.editedRows = new Set(this.editedRows).add(row);
    }
  }

  onSelectAlias(aliasId: string, row: Row) {
    const selectedAlias = this.aliasList.find(a => a.id === aliasId);
    if (!selectedAlias) {
      return;
    }
    row.expression = selectedAlias.expression;
    row.freeTextMode = false;
    this.unsavedRow(row, this.selectedTemplate.id, false);
  }

  buildAliasOptions(row: Row): OptionsListMulti[] {
    if (!this.aliasList) {
      return [];
    }

    return this.aliasList.map(alias => ({
      id: alias.id,
      name: alias.expression,
      badgeText: alias.name,
      selected: alias.expression === row.expression
    }));
  }

  getSelectedAliasId(row: Row): string {
    return this.aliasList?.find(a => a.expression === row.expression)?.id ?? '';
  }

  override clickEdit(i: number, newRow?: Row, cloneItem?: boolean) {
    const modalRef = this.modalService.open(TemplateItemEditorModalComponent, { ariaLabelledBy: 'modal-basic-title' });
    const item = { ...this.rows[i] };
    modalRef.componentInstance.item = JSON.stringify(newRow || item);
    modalRef.componentInstance.isActuator = this.isActuator;
    modalRef.componentInstance.cloneItem = cloneItem;
    modalRef.result.then(
      (result: string) => {
        if (result.localeCompare('Cance click') !== 0) {
          const row = JSON.parse(result) as Row;
          this.unsaved(this.templateId);
          if (newRow) {
            this.rows.splice(i + 1, 0, row);
            this.ensureRowUiState(this.rows[i + 1]);
          } else {
            this.rows[i] = row;
            this.ensureRowUiState(this.rows[i]);
          }
          if (this.rows.length !== this.filteredRows.length) {
            this.setSearch(this.searchString);
          }
        }
      },
      reason => {
        // console.log(`Dismissed ${reason}`); // ModalDismissReasons.BACKDROP_CLICK
      }
    );
  }

  override clickEditHeaders(i: number, newRow?: Row) {
    const modalRef = this.modalService.open(TemplateItemEditorModalComponent, { ariaLabelledBy: 'modal-basic-title' });
    const item = { ...this.rowsHeaders[i] };
    modalRef.componentInstance.item = JSON.stringify(newRow || item);
    modalRef.componentInstance.isActuator = this.isActuator;
    modalRef.result.then(
      (result: string) => {
        if (result.localeCompare('Cance click') !== 0) {
          const row = JSON.parse(result) as Row;
          this.unsaved(this.templateId);
          if (newRow) {
            this.rowsHeaders.splice(i + 1, 0, row);
            this.ensureRowUiState(this.rowsHeaders[i + 1]);
          } else {
            this.rowsHeaders[i] = row;
            this.ensureRowUiState(this.rowsHeaders[i]);
          }
          if (this.rowsHeaders.length !== this.filteredRowsHeaders.length) {
            this.setSearch(this.searchString);
          }
        }
      },
      reason => {
        // console.log(`Dismissed ${reason}`); // ModalDismissReasons.BACKDROP_CLICK
      }
    );
  }

  private ensureRowUiState(row: Row): void {
    if (!row) {
      return;
    }

    if (!row.freeTextToggleId) {
      row.freeTextToggleId = `freeTextToggle-${this.rowUiIdCounter++}`;
    }

    if (row.aliasOptionsLoader === undefined) {
      row.aliasOptionsLoader = () => this.buildAliasOptions(row);
    }

    if (row.freeTextMode === undefined) {
      const hasMatchingAlias = !!row.expression && this.aliasList?.some(alias => alias.expression === row.expression);
      row.freeTextMode = !hasMatchingAlias;
    }
  }
}
